import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import FrontechLogo from './FrontechLogo';
import Seo from './marketing/Seo';

export default function Refunds() {
  return (
    <div style={{ background: 'var(--color-base)', color: 'var(--color-text-primary)', minHeight: '100vh' }}>
      <Seo
        title="Refunds Policy · FRONTECH"
        description="How FRONTECH handles refunds for Front SaaS subscriptions."
        path="/refunds"
        noindex
      />
      <nav className="border-b" style={{ borderColor: 'var(--color-border)' }}>
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80">
            <FrontechLogo size={22} variant="icon" color="var(--color-accent)" />
            <span className="font-heading text-sm font-semibold">Front</span>
            <span className="text-xs font-mono" style={{ color: 'var(--color-text-muted)' }}>by FRONTECH</span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm hover:opacity-80"
            style={{ color: 'var(--color-text-secondary)' }}>
            <ArrowLeft size={14} /> Back to home
          </Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-5 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-2">Refund Policy</h1>
        <p className="text-xs font-mono uppercase tracking-widest mb-10" style={{ color: 'var(--color-text-muted)' }}>
          Effective date: November 6, 2026
        </p>

        <div className="space-y-8 text-[15px] leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>1. Free plan</h2>
            <p>
              Our Free tier is available at no cost. No payment, no refund.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>2. Monthly subscriptions</h2>
            <p>
              You can cancel a monthly subscription at any time from your billing page or by emailing{' '}
              <span style={{ color: 'var(--color-accent)' }}>support@frontech-consulting.com</span>. Cancellation takes effect at the end
              of the current billing period — you keep access to your paid tier until then. We do not issue partial refunds for
              unused portions of a monthly billing period.
            </p>
            <p className="mt-3">
              If you were charged in error (double billing, incorrect tier, failed activation), contact us within 14 days of the
              charge and we will issue a full refund via Paddle, our payment processor.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>3. Annual subscriptions</h2>
            <p>
              Annual subscriptions are eligible for a full refund within 14 days of the initial purchase, provided total usage
              during that window has not exceeded 10% of the annual plan's limits (voice minutes, chat messages, or agents
              created). This mirrors standard consumer-protection windows under EU / UK / US law.
            </p>
            <p className="mt-3">
              After the 14-day window, annual subscriptions are non-refundable, but you may cancel to prevent the next renewal.
              Cancellation preserves your access through the end of the paid year.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>4. Enterprise plans</h2>
            <p>
              Enterprise agreements are subject to the refund terms in your signed contract. Contact your account manager at{' '}
              <span style={{ color: 'var(--color-accent)' }}>sales@frontech-consulting.com</span>.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>5. Service failures</h2>
            <p>
              If Front experiences a sustained outage (more than 24 continuous hours) that prevents you from using the service
              you paid for, contact us and we will issue a pro-rata credit or refund at your choice, regardless of billing cycle.
              This applies to first-party outages only, not to downstream third-party providers (OpenAI, Supabase, etc.) whose
              availability we do not control.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>6. How to request a refund</h2>
            <p>
              Email <span style={{ color: 'var(--color-accent)' }}>support@frontech-consulting.com</span> with your account email,
              the transaction date, and a brief description of the reason. Refund-eligible requests are processed within 5 business
              days. Refunds are issued to the original payment method by Paddle, our merchant of record. Depending on your bank or
              card issuer, funds typically appear within 5–10 business days.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>7. Chargebacks</h2>
            <p>
              Please contact us before initiating a chargeback with your bank. In most cases we can resolve billing issues faster
              and cheaper than a formal chargeback, which typically incurs a fee that we may pass on to the account holder if the
              chargeback is later determined to be unwarranted.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>8. Governing law</h2>
            <p>
              This refund policy is governed by the laws of the Islamic Republic of Pakistan and forms part of our{' '}
              <Link to="/terms" className="underline hover:opacity-80" style={{ color: 'var(--color-accent)' }}>Terms of Service</Link>.
              Statutory consumer-protection rights in your jurisdiction always apply and are not limited by this policy.
            </p>
          </section>
        </div>
      </main>

      <footer className="py-10 px-5 mt-8" style={{ borderTop: '1px solid var(--color-border)' }}>
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            &copy; {new Date().getFullYear()} FRONTECH Consulting. All rights reserved.
          </span>
          <div className="flex items-center gap-5 text-xs">
            <Link to="/privacy" style={{ color: 'var(--color-text-muted)' }} className="hover:opacity-80">Privacy</Link>
            <Link to="/terms" style={{ color: 'var(--color-text-muted)' }} className="hover:opacity-80">Terms</Link>
            <Link to="/refunds" style={{ color: 'var(--color-text-muted)' }} className="hover:opacity-80">Refunds</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
